/**
 * Per-path write serialization for the WebDAV engine (project
 * conditional-writes-are-not-atomic, 2026-10-31).
 *
 * A PUT carrying If-Match promises the resource was still that version when
 * it was replaced. That holds only if the precondition check and the write
 * are one step for the resource; the engine awaits the handle, the writable
 * and the close between them, and ten simultaneous writers all passed the
 * check (five of their writes survived, measured 2026-09-24). The handler
 * now runs every write method under a lock on its path, held from the check
 * through the write, so one writer wins and the rest see the new version
 * and get 412.
 *
 * In a browser the lock is a Web Lock (`navigator.locks`): origin-wide, so
 * it also covers the service worker's local OPFS mount and a second tab
 * serving the same directory, which an in-memory mutex never could. The
 * name carries the root directory's name, so two engines over different
 * directories with the same name serialize needlessly but harmlessly, and
 * two contexts over the same directory serialize correctly. Where Web Locks
 * are missing (Node, older WebKit) the lock is an in-process chain per
 * path, which is exact for the one engine a Node process runs.
 *
 * Several paths (COPY and MOVE hold source and destination) are taken in
 * sorted order, so two crossing transfers cannot wait on each other.
 */

/**
 * @param {object} [options]
 * @param {() => string} [options.scope] names the store the paths belong to
 *   (the root directory's name); read at each request, since the root is
 *   set after the engine is built
 * @param {LockManager|null} [options.locks] the Web Locks manager; defaults
 *   to `navigator.locks`, null for the in-process fallback
 */
export function createPathLocks({ scope = () => '', locks = defaultLocks() } = {}) {
    const chains = new Map() // path → promise that settles when its last holder releases

    const name = (path) => `browserdav|${scope()}|${path}`

    async function withLock(path, fn) {
        if (locks) return locks.request(name(path), () => fn())
        const previous = chains.get(path) || Promise.resolve()
        let release
        const mine = new Promise((resolve) => { release = resolve })
        const tail = previous.then(() => mine)
        chains.set(path, tail)
        await previous
        try {
            return await fn()
        } finally {
            release()
            if (chains.get(path) === tail) chains.delete(path)
        }
    }

    function withLocks(paths, fn) {
        const sorted = [...new Set(paths)].sort()
        const run = (i) => (i === sorted.length ? fn() : withLock(sorted[i], () => run(i + 1)))
        return run(0)
    }

    return { withLock, withLocks, usesWebLocks: !!locks }
}

function defaultLocks() {
    const locks = globalThis.navigator?.locks
    return locks && typeof locks.request === 'function' ? locks : null
}
